import { Chip, Skeleton, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { To } from "react-router";

import {
    MapPanel,
    MapPanelCollapsableContent,
    MapPanelHeader,
    MapPanelScrollableArea,
    MapPanelToggleCollapseButton,
} from "~/modules/map/components/MapPanel";
import { AreaCard, AreaCardVisitItem } from "~/modules/plannerV2/components/AreaCard";

import { PlannerV2AreasScreenAreaFragment } from "../../queries.generated";

const MAX_AREAS_LISTED = 10;

export function PlannerV2AreaListMapPanel({
    title,
    areas,
    panelIsOpen,
    onPanelOpenToggle,
    loading,
    visitOpenHref,
    areaNameHref,
}: {
    title: string;
    areas: PlannerV2AreasScreenAreaFragment[];
    panelIsOpen: boolean;
    loading: boolean;
    onPanelOpenToggle: () => void;
    visitOpenHref: (plannerAreaId: string, plannerVisitId: string) => To;
    areaNameHref: (plannerAreaId: string) => To;
}) {
    const { t } = useTranslation("plannerV2");

    const areasToRender = useMemo(() => {
        return [...areas]
            .sort((a, b) => {
                const aVal = a.earliestPlannerVisitDaysRemaining ?? null;
                const bVal = b.earliestPlannerVisitDaysRemaining ?? null;
                if (aVal === null && bVal === null) return 0;
                if (aVal === null) return 1;
                if (bVal === null) return -1;
                return aVal - bVal;
            })
            .slice(0, MAX_AREAS_LISTED);
    }, [areas]);

    return (
        <MapPanel collapsable collapsed={panelIsOpen === false}>
            <MapPanelHeader sx={{ justifyContent: "space-between" }}>
                <Typography flexGrow={1} variant="h6">
                    {title}
                </Typography>
                <Chip label={t("areaListMapPanel.areas", { count: areas.length })} />
                <MapPanelToggleCollapseButton collapsed={panelIsOpen === false} onToggleCollapse={onPanelOpenToggle} />
            </MapPanelHeader>
            <MapPanelCollapsableContent collapsed={panelIsOpen === false}>
                <MapPanelScrollableArea sx={{ padding: 2, display: "flex", gap: 2, flexDirection: "column" }}>
                    {areas.length === 0 &&
                        (loading ? (
                            <Skeleton width={360} height={56} />
                        ) : (
                            <Typography>{t("areaListMapPanel.noAreasFound")}</Typography>
                        ))}
                    {areasToRender.map(area => (
                        <AreaCard key={area.id} area={area} areaNameHref={areaNameHref}>
                            {area.plannerVisits.map(visit => (
                                <AreaCardVisitItem
                                    showFieldworkers
                                    key={visit.id}
                                    visit={visit}
                                    href={visitOpenHref(area.id, visit.id)}
                                />
                            ))}
                        </AreaCard>
                    ))}
                    {areas.length > MAX_AREAS_LISTED && (
                        <Typography variant="caption" textAlign="center">
                            {t("areaListMapPanel.areasNotShown", { count: areas.length - MAX_AREAS_LISTED })}
                        </Typography>
                    )}
                </MapPanelScrollableArea>
            </MapPanelCollapsableContent>
        </MapPanel>
    );
}
